import { Box, Typography } from '@mui/material';
import {
  getSpotifyEmbedUrl,
  isAppleMusicUrl,
  MediaType,
} from '../utils/mediaUtils';

interface MediaRendererProps {
  url: string;
  embedUrl: string;
  mediaType: MediaType;
  onClick?: () => void;
  variant?: 'preview' | 'modal';
  maxHeight?: number;
}

const ClickableWrapper = ({
  children,
  onClick,
  overlay = false,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  overlay?: boolean;
}) => {
  if (!onClick) return <>{children}</>;

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        position: 'relative',
        ...(overlay && {
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '10px',
            pointerEvents: 'none',
          },
        }),
      }}
    >
      {children}
    </Box>
  );
};

const IframeRenderer = ({
  src,
  title,
  onClick,
  variant = 'preview',
  allow,
  sandbox,
  allowFullScreen,
  pointerEvents,
  maintainAspectRatio = false,
}: {
  src: string;
  title: string;
  onClick?: () => void;
  variant?: 'preview' | 'modal';
  allow?: string;
  sandbox?: string;
  allowFullScreen?: boolean;
  pointerEvents?: 'none' | 'auto';
  maintainAspectRatio?: boolean;
}) => {
  // YouTube/Vimeo standard aspect ratio is 16:9
  const aspectRatio = 56.25; // 9/16 * 100 = 56.25%

  const iframe = maintainAspectRatio ? (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        paddingBottom: `${aspectRatio}%`, // 16:9 aspect ratio
        height: 0,
        overflow: 'hidden',
        borderRadius: '10px',
        ...(variant === 'preview' && {
          boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
        }),
      }}
    >
      <Box
        component="iframe"
        src={src}
        title={title}
        allow={allow}
        sandbox={sandbox}
        allowFullScreen={allowFullScreen}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: '0',
          borderRadius: '10px',
          ...(pointerEvents && { pointerEvents }),
        }}
      />
    </Box>
  ) : (
    <Box
      component="iframe"
      src={src}
      title={title}
      allow={allow}
      sandbox={sandbox}
      allowFullScreen={allowFullScreen}
      sx={{
        width: '100%',
        height: 'auto',
        border: '0',
        borderRadius: '10px',
        ...(variant === 'preview' && {
          boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
        }),
        ...(pointerEvents && { pointerEvents }),
      }}
    />
  );

  return <ClickableWrapper onClick={onClick}>{iframe}</ClickableWrapper>;
};

export const MediaRenderer = ({
  url,
  embedUrl,
  mediaType,
  onClick,
  variant = 'preview',
  maxHeight,
}: MediaRendererProps) => {
  switch (mediaType) {
    case 'image':
      return (
        <Box
          component="img"
          src={embedUrl}
          alt={url}
          onClick={onClick}
          title={onClick ? 'Click to expand' : undefined}
          sx={{
            ...(variant === 'modal'
              ? {
                  maxWidth: '100%',
                  maxHeight: maxHeight ? `${maxHeight}px` : '90vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                }
              : {
                  width: '100%',
                  ...(maxHeight && { maxHeight: `${maxHeight}px` }),
                  objectFit: 'cover',
                }),
            borderRadius: variant === 'modal' ? '8px' : '10px',
            boxShadow:
              variant === 'modal'
                ? '0px 3px 16px 2px rgba(0, 0, 0, 0.12), 0px 9px 12px 1px rgba(0, 0, 0, 0.14), 0px 5px 6px -3px rgba(0, 0, 0, 0.20)'
                : '0 1px 6px rgba(0,0,0,0.08)',
            cursor: onClick ? 'pointer' : 'default',
            transition: onClick ? 'transform 0.2s ease' : undefined,
            '&:hover': onClick
              ? {
                  transform: 'scale(1.02)',
                }
              : {},
          }}
        />
      );

    case 'audio':
      if (url.includes('spotify.com')) {
        const spotifyEmbedUrl = getSpotifyEmbedUrl(url);
        if (!spotifyEmbedUrl) return null;
        return (
          <IframeRenderer
            src={spotifyEmbedUrl}
            title="Spotify preview"
            onClick={onClick}
            variant={variant}
            allow="encrypted-media"
          />
        );
      }
      if (isAppleMusicUrl(url)) {
        return (
          <IframeRenderer
            src={url}
            title="Apple Music preview"
            onClick={onClick}
            variant={variant}
            allow="encrypted-media"
            sandbox="allow-same-origin allow-scripts allow-popups allow-presentation"
          />
        );
      }
      return (
        <ClickableWrapper onClick={onClick}>
          <audio
            controls
            style={{
              width: '100%',
              borderRadius: '10px',
            }}
          >
            <source src={embedUrl} />
            Your browser does not support the audio element.
          </audio>
        </ClickableWrapper>
      );

    case 'youtube':
    case 'vimeo':
    case 'loom':
      return (
        <IframeRenderer
          src={embedUrl}
          title="Media preview"
          onClick={onClick}
          variant={variant}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          maintainAspectRatio={true}
        />
      );

    case 'video':
      return (
        <ClickableWrapper onClick={onClick} overlay>
          <video
            controls
            style={{
              ...(variant === 'modal'
                ? {
                    maxWidth: '100%',
                    maxHeight: maxHeight ? `${maxHeight}px` : '90vh',
                    width: 'auto',
                    height: 'auto',
                  }
                : {
                    width: '100%',
                    height: '100%',
                  }),
              borderRadius: '10px',
            }}
          >
            <source src={embedUrl} />
            Your browser does not support the video tag.
          </video>
        </ClickableWrapper>
      );

    case 'pdf':
      return (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height:
              variant === 'modal'
                ? maxHeight
                  ? `${maxHeight}px`
                  : '90vh'
                : '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {onClick && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                borderRadius: '10px',
                zIndex: 2,
                cursor: 'pointer',
              }}
              onClick={onClick}
            />
          )}
          <Box
            component="iframe"
            src={embedUrl}
            title="PDF preview"
            sx={{
              width: '100%',
              height: '100%',
              border: '0',
              borderRadius: '10px',
              ...(variant === 'preview' && {
                boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
                pointerEvents: onClick ? 'none' : 'auto',
              }),
            }}
          />
        </Box>
      );

    default:
      return (
        <Typography
          sx={{
            fontSize: '14px',
            color: '#475467',
            wordBreak: 'break-all',
          }}
        >
          {url}
        </Typography>
      );
  }
};
